import React, { useEffect, useMemo, useState } from "react";
import { Input, Avatar, message, Button, Space, Card, Tag, Typography, Col } from "antd";
import {
  CommentOutlined,
  SendOutlined,
  EditOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { formatDpatCommentUserRole, isDpatQualityAssuranceCommentRole } from "../utils/dpatCommentRoleLabel";
import useAuth from "../hooks/useAuth";
import instance from "../api/cmsapi";
import { useCreateComment, invalidateCommentSummaryQueries, useGetCommentsBySpecificTable } from "service/comment.service";
import { useDpatCommentFeedback } from "../context/DpatCommentFeedbackContext";
import { useQueryClient } from "react-query";
import {
  commentHasGaps,
  formatDpatGapsField,
  hasDpatGapsContent,
  mergeDddpDataForComment,
  parseDpatGapsFromComment,
} from "../utils/dpatGapsHelpers";
import DpatManualScorePanel from "./DPAT/setup/DpatManualScorePanel";
import {
  findSummaryRowForSlug,
  indicatorKindFromSlug,
  parseManualScoresFromComments,
  resolveSystemScoreDisplay,
  slugFromTableCommentedId,
} from "../utils/dpatManualScoreHelpers";

function Comment({ data, year, districtId, tableCommentedId, children, hideComment, manualScoreConfig }) {
  const { user } = useAuth();
  const { hideCommentAuthorNames, assessmentWorkflowStatus, scoreSheetSummary } = useDpatCommentFeedback();
  const queryClient = useQueryClient();
  const createCommentMutation = useCreateComment();
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [comments, setComments] = useState([]);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editText, setEditText] = useState({});
  const [showGapsInput, setShowGapsInput] = useState(false);
  const [gapsIssues, setGapsIssues] = useState("");
  const [gapsRecommendation, setGapsRecommendation] = useState("");
  const [editingGapsId, setEditingGapsId] = useState(null);
  const [editGapsDraft, setEditGapsDraft] = useState({});

  const roleNames = user?.user?.userRoles?.map((role) => role.name) ?? [];
  const isTechnicalTeam = roleNames.includes("DPAT TECHNICAL TEAM");
  const isQualityAssurance = roleNames.includes("DPAT QUALITY ASSURANCE");
  const isPetitionCommittee = roleNames.includes("DPAT PETITION COMMITTEE");
  const isSuperuser = roleNames.includes("Superuser");
  const isDistrictUser = roleNames.includes("DPAT DISTRICT USERS");
  const canViewScoresAndComments =
    isTechnicalTeam || isQualityAssurance || isPetitionCommittee || isSuperuser;
  // Technical Team: comments + gaps during "Start"
  const canEditTechnicalComments =
    (isTechnicalTeam || isSuperuser) && assessmentWorkflowStatus === "Start";
  // QA: comments only (no gaps) during "Pending"
  const canEditQaComments =
    (isQualityAssurance || isSuperuser) && assessmentWorkflowStatus === "Pending";
  const canEditGaps = canEditTechnicalComments;
  const isActingAsQa = canEditQaComments && !canEditTechnicalComments;
  // Short role keys: "DQA" (3 chars) / "DTT" (3 chars)
  const normalizedUserRole = isActingAsQa ? "DQA" : "DTT";
  const currentUsername = user?.user?.username || "";
  const currentFullName = user?.user?.fullName || "";
  const type = "DPAT";

  const { Text: AntText } = Typography;

  const normalizeStoredRole = (r) => String(r || "").replace(/\s+/g, "_").toUpperCase();

  const yearForApi = year == null || year === "" ? null : Number.isFinite(Number(year)) ? Number(year) : year;

  const { data: commentsResponse } = useGetCommentsBySpecificTable(
    districtId,
    yearForApi,
    type,
    tableCommentedId,
    {
      onError: (error) => {
        console.error("Failed to fetch comments:", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
        });
        message.error("Failed to fetch comments");
      },
    },
  );

  const commentTextFromRow = (c) => (c?.comments != null && c.comments !== "" ? c.comments : c?.comment) ?? "";
  const gapsTextFromRow = (c) => {
    const { issues, recommendation } = parseDpatGapsFromComment(c);
    return formatDpatGapsField(issues, recommendation) || c?.gaps || "";
  };

  const formatCommentDate = (d) => {
    if (d == null) return "—";
    if (Array.isArray(d)) return d.filter((x) => x != null).join("/");
    return String(d);
  };

  // Seed from indicator snapshot only when local comments are empty.
  // Never overwrite API/manual-score rows with a stale or empty snapshot.
  useEffect(() => {
    if (!Array.isArray(data?.comments)) return;
    const filteredComments = data.comments.filter(
      (comment) =>
        comment.tableCommented === tableCommentedId &&
        String(comment.districtId) === String(districtId)
    );
    setComments((prev) => (prev.length > 0 ? prev : filteredComments));
  }, [data, year, tableCommentedId, districtId]);

  useEffect(() => {
    const rows = Array.isArray(commentsResponse)
      ? commentsResponse
      : Array.isArray(commentsResponse?.data)
        ? commentsResponse.data
        : null;
    if (rows) setComments(rows);
  }, [commentsResponse]);

  // Check if QA score disagrees with Technical Team score
  const qaDisagreesWithTechnicalTeam = useMemo(() => {
    if (!isActingAsQa) return false;
    const scores = parseManualScoresFromComments(comments);
    const qaScore = String(scores.qualityAssuranceScore || "").trim();
    if (!qaScore) return false;

    const slug = slugFromTableCommentedId(tableCommentedId);
    const kind = indicatorKindFromSlug(slug);
    const summaryRow = findSummaryRowForSlug(scoreSheetSummary, slug);
    const systemDisplay = resolveSystemScoreDisplay({
      indicatorKind: kind,
      summaryRow,
      manualScoreConfig,
    });
    const techScore =
      String(scores.technicalTeamScore || "").trim() ||
      String(systemDisplay || "").trim();

    return qaScore !== techScore;
  }, [comments, tableCommentedId, scoreSheetSummary, manualScoreConfig, isActingAsQa]);

  // QA can only comment when their score disagrees with Technical Team
  const canEditComments = canEditTechnicalComments || (canEditQaComments && qaDisagreesWithTechnicalTeam);

  // Auto-hide comment input if QA score matches Technical Team
  useEffect(() => {
    if (isActingAsQa && !qaDisagreesWithTechnicalTeam) {
      setShowCommentInput(false);
    }
  }, [isActingAsQa, qaDisagreesWithTechnicalTeam]);

  const findExistingUserComment = () =>
    comments.find(
      (comment) =>
        comment.tableCommented === tableCommentedId &&
        normalizeStoredRole(comment.userRole) === normalizedUserRole &&
        String(comment.districtId) === String(districtId) &&
        comment.username === currentUsername
    ) ?? null;

  const upsertCommentRow = async (payload, existingComment) => {
    if (existingComment?.id) {
      const response = await instance.put(`comments/${existingComment.id}`, payload);
      return response?.data ?? { ...existingComment, ...payload, id: existingComment.id };
    }

    const response = await instance.post("comments", payload);
    return response?.data ?? { ...payload, id: payload.id || Date.now() };
  };

  const refreshCommentSummaries = () => {
    invalidateCommentSummaryQueries(queryClient);
  };

  const handleCommentSubmit = async (submitText = commentText) => {
    if (!canEditComments) {
      message.error("You cannot add or edit comments at this stage.");
      return;
    }
    if (!submitText.trim()) {
      message.error("Comment cannot be empty");
      return;
    }
    if (!currentUsername || !districtId || yearForApi == null) {
      message.error("User or district information is missing");
      return;
    }

    const existingComment = findExistingUserComment();

    console.log("=== Comment Submission ===", {
      normalizedUserRole,
      isActingAsQa,
      existingComment: existingComment ? { id: existingComment.id, userRole: existingComment.userRole } : null,
    });

    const commentDate = new Date().toISOString().split("T")[0].split("-").map(Number);
    const payload = {
      id: existingComment ? existingComment.id : 0,
      username: currentUsername,
      fullName: currentFullName,
      userRole: normalizedUserRole,
      type: "DPAT",
      districtId: districtId,
      year: yearForApi,
      tableCommented: tableCommentedId,
      comments: submitText,
      gaps: existingComment ? gapsTextFromRow(existingComment) || "" : "",
      commentDate: existingComment ? existingComment.commentDate : commentDate,
      updateDate: commentDate,
      dddpDataDate: commentDate,
      dddpData: mergeDddpDataForComment(existingComment, data, tableCommentedId),
    };

    console.log("Payload:", JSON.stringify(payload, null, 2));

    try {
      if (existingComment) {
        const saved = await upsertCommentRow(payload, existingComment);
        setComments((prev) =>
          prev.map((comment) => (comment.id === saved.id ? saved : comment))
        );
        refreshCommentSummaries();
        message.success("Comment updated successfully");
        if (editingCommentId) {
          setEditingCommentId(null);
          setEditText({});
        } else {
          setCommentText("");
          setShowCommentInput(false);
        }
      } else {
        createCommentMutation.mutate(payload, {
          onSuccess: (response) => {
            const savedComment = response?.data ?? {
              ...payload,
              id: payload.id || Date.now(),
            };
            setComments((prev) => [...prev, savedComment]);
            refreshCommentSummaries();
            message.success("Comment saved successfully");
            setCommentText("");
            setShowCommentInput(false);
          },
          onError: (error) =>
            message.error(`Failed to save comment: ${error.response?.data?.message || error.message}`),
        });
      }
    } catch (error) {
      console.error("Failed to save comment:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      message.error(`Failed to save comment: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleGapsSubmit = async (issues = gapsIssues, recommendation = gapsRecommendation) => {
    if (!canEditGaps) {
      message.error("Only DPAT TECHNICAL TEAM can add or edit capacity gaps.");
      return;
    }
    if (!hasDpatGapsContent(issues, recommendation)) {
      message.error("Enter issues and/or recommendation");
      return;
    }
    if (!currentUsername || !districtId || yearForApi == null) {
      message.error("User or district information is missing");
      return;
    }

    const existingComment = findExistingUserComment();

    const commentDate = new Date().toISOString().split("T")[0].split("-").map(Number);
    const payload = {
      id: existingComment ? existingComment.id : 0,
      username: currentUsername,
      fullName: currentFullName,
      userRole: normalizedUserRole,
      type: "DPAT",
      districtId: districtId,
      year: yearForApi,
      tableCommented: tableCommentedId,
      comments: existingComment ? commentTextFromRow(existingComment) || "" : "",
      gaps: formatDpatGapsField(issues, recommendation),
      commentDate: existingComment ? existingComment.commentDate : commentDate,
      updateDate: commentDate,
      dddpDataDate: commentDate,
      dddpData: mergeDddpDataForComment(existingComment, data, tableCommentedId, {
        issues,
        recommendation,
      }),
    };

    try {
      const saved = await upsertCommentRow(payload, existingComment);
      setComments((prev) => {
        if (existingComment?.id) {
          return prev.map((comment) => (comment.id === saved.id ? saved : comment));
        }
        return [...prev, saved];
      });
      refreshCommentSummaries();
      message.success(existingComment ? "Gaps updated successfully" : "Gaps added successfully");
      setGapsIssues("");
      setGapsRecommendation("");
      setShowGapsInput(false);
      setEditingGapsId(null);
      setEditGapsDraft({});
    } catch (error) {
      console.error("Failed to save gaps:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      message.error(`Failed to save gaps: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleEditComment = (comment) => {
    if (!canEditComments) {
      message.error("You cannot edit comments at this stage.");
      return;
    }
    if (comment.username !== currentUsername) {
      message.error("You can only edit your own comments.");
      return;
    }
    setEditingCommentId(comment.id);
    setEditText({ ...editText, [comment.id]: comment.comments });
  };

  const handleEditGaps = (comment) => {
    if (!canEditGaps) {
      message.error("Only DPAT TECHNICAL TEAM can edit capacity gaps.");
      return;
    }
    if (comment.username !== currentUsername) {
      message.error("You can only edit your own gaps.");
      return;
    }
    setEditingGapsId(comment.id);
    const parsed = parseDpatGapsFromComment(comment);
    setEditGapsDraft({
      ...editGapsDraft,
      [comment.id]: { issues: parsed.issues, recommendation: parsed.recommendation },
    });
  };

  const handleDeleteComment = async (commentId) => {
    const comment = comments.find((c) => c.id === commentId);
    if (comment.username !== currentUsername) {
      message.error("You can only delete your own comments.");
      return;
    }
    try {
      await instance.delete(`comments/${commentId}`);
      setComments(comments.filter((comment) => comment.id !== commentId));
      refreshCommentSummaries();
      setEditingCommentId(null);
      setEditingGapsId(null);
      setCommentText("");
      setGapsIssues("");
      setGapsRecommendation("");
      setEditText({});
      setEditGapsDraft({});
      setShowCommentInput(false);
      setShowGapsInput(false);
      message.success("Comment deleted successfully");
    } catch (error) {
      console.error("Failed to delete comment:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      message.error("Failed to delete comment");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleCommentSubmit();
    }
  };

  const handleEditKeyPress = (e, commentId) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleCommentSubmit(editText[commentId]);
    }
  };

  const handleEditGapsKeyPress = (e, commentId) => {
    if (e.key === "Enter" && e.ctrlKey) {
      e.preventDefault();
      const draft = editGapsDraft[commentId] || {};
      handleGapsSubmit(draft.issues, draft.recommendation);
    }
  };

  const renderGapsFields = ({
    issues,
    recommendation,
    onIssuesChange,
    onRecommendationChange,
    onKeyPress,
    minRows = 3,
  }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
      <div>
        <AntText strong style={{ display: "block", marginBottom: 6, fontSize: 13 }}>
          Issues
        </AntText>
        <Input.TextArea
          value={issues}
          onChange={(e) => onIssuesChange(e.target.value)}
          onKeyPress={onKeyPress}
          placeholder="Describe the issues identified…"
          autoSize={{ minRows, maxRows: 8 }}
          style={{
            width: "100%",
            borderRadius: 8,
            padding: "10px 12px",
            background: "#f5f7fa",
          }}
        />
      </div>
      <div>
        <AntText strong style={{ display: "block", marginBottom: 6, fontSize: 13 }}>
          Recommendation
        </AntText>
        <Input.TextArea
          value={recommendation}
          onChange={(e) => onRecommendationChange(e.target.value)}
          onKeyPress={onKeyPress}
          placeholder="Describe the recommendation…"
          autoSize={{ minRows, maxRows: 8 }}
          style={{
            width: "100%",
            borderRadius: 8,
            padding: "10px 12px",
            background: "#f5f7fa",
          }}
        />
      </div>
    </div>
  );

  const renderGapsDisplay = (comment) => {
    const { issues, recommendation } = parseDpatGapsFromComment(comment);
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {issues ? (
          <div>
            <AntText strong style={{ display: "block", marginBottom: 4, fontSize: 13 }}>
              Issues
            </AntText>
            <AntText style={{ fontSize: 16, whiteSpace: "pre-wrap" }}>{issues}</AntText>
          </div>
        ) : null}
        {recommendation ? (
          <div>
            <AntText strong style={{ display: "block", marginBottom: 4, fontSize: 13 }}>
              Recommendation
            </AntText>
            <AntText style={{ fontSize: 16, whiteSpace: "pre-wrap" }}>{recommendation}</AntText>
          </div>
        ) : null}
      </div>
    );
  };

  const canShowCommentInput = () => {
    if (!canEditComments) {
      return false;
    }
    if (editingCommentId) {
      return false;
    }
    return !comments.some(
      (comment) =>
        comment.tableCommented === tableCommentedId &&
        normalizeStoredRole(comment.userRole) === normalizedUserRole &&
        String(comment.districtId) === String(districtId) &&
        comment.username === currentUsername &&
        commentTextFromRow(comment).trim()
    );
  };

  const canAddGaps = () => {
    if (!canEditGaps) {
      return false;
    }
    return !comments.some(
      (comment) =>
        comment.tableCommented === tableCommentedId &&
        normalizeStoredRole(comment.userRole) === normalizedUserRole &&
        String(comment.districtId) === String(districtId) &&
        comment.username === currentUsername &&
        commentHasGaps(comment)
    );
  };

  const handleCommentButtonClick = () => {
    if (!canEditComments) {
      message.info(
        canEditQaComments && !qaDisagreesWithTechnicalTeam
          ? "Your QA score must differ from the Technical Team score to add a comment."
          : isQualityAssurance
            ? "Quality Assurance can add comments when the assessment is Pending."
            : "Only the Technical Team or Quality Assurance can add comments.",
      );
      return;
    }
    if (canShowCommentInput()) {
      setShowCommentInput(!showCommentInput);
    } else {
      message.info("You have already added a comment for this indicator.");
    }
  };

  const handleGapsButtonClick = () => {
    if (!canEditGaps) {
      message.info("Only DPAT TECHNICAL TEAM can add capacity gaps.");
      return;
    }
    if (canAddGaps()) {
      setShowGapsInput(!showGapsInput);
      setGapsIssues("");
      setGapsRecommendation("");
    } else {
      message.info("You have already added gaps for this indicator.");
    }
  };

  const DPAT_PRIMARY = "#16325a";

  const renderCommentActionBar = () =>
    !hideComment && canEditComments ? (
      <Space wrap size={10} className="dpat-indicator-feedback__actions">
        <Button
          type={showCommentInput ? "primary" : "default"}
          icon={<CommentOutlined />}
          onClick={handleCommentButtonClick}
          style={
            showCommentInput
              ? { backgroundColor: DPAT_PRIMARY, borderColor: DPAT_PRIMARY }
              : { borderColor: DPAT_PRIMARY, color: DPAT_PRIMARY }
          }
        >
          {showCommentInput ? "Close comment" : "Add comment"}
        </Button>
        {canEditGaps && (
          <Button
            type={showGapsInput ? "primary" : "default"}
            icon={<PlusCircleOutlined />}
            onClick={handleGapsButtonClick}
            style={
              showGapsInput
                ? { backgroundColor: DPAT_PRIMARY, borderColor: DPAT_PRIMARY }
                : { borderColor: DPAT_PRIMARY, color: DPAT_PRIMARY }
            }
          >
            {showGapsInput ? "Close capacity gaps" : "Add capacity gaps"}
          </Button>
        )}
      </Space>
    ) : null;

  const renderCommentPanels = () =>
    !hideComment && canEditComments ? (
      <Space direction="vertical" size={14} style={{ width: "100%" }} className="dpat-indicator-feedback__panels">
        {showCommentInput && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, width: "100%" }}>
            <Avatar
              src={
                user?.image ||
                "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTL_JlCFnIGX5omgjEjgV9F3sBRq14eTERK9w&s"
              }
              style={{ flexShrink: 0 }}
              size={36}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Input.TextArea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Write your comment… (Shift+Enter for new line)"
                autoSize={{ minRows: 3, maxRows: 8 }}
                style={{
                  width: "100%",
                  borderRadius: 8,
                  padding: "10px 12px",
                  background: "#f5f7fa",
                }}
              />
              {commentText.trim() && (
                <div style={{ marginTop: 8, textAlign: "right" }}>
                  <Button
                    type="primary"
                    size="small"
                    icon={<SendOutlined />}
                    onClick={() => handleCommentSubmit()}
                    style={{ backgroundColor: DPAT_PRIMARY, borderColor: DPAT_PRIMARY }}
                  >
                    Save Comment
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {canEditGaps && showGapsInput && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, width: "100%" }}>
            <Avatar
              src={
                user?.image ||
                "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTL_JlCFnIGX5omgjEjgV9F3sBRq14eTERK9w&s"
              }
              style={{ flexShrink: 0 }}
              size={36}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              {renderGapsFields({
                issues: gapsIssues,
                recommendation: gapsRecommendation,
                onIssuesChange: setGapsIssues,
                onRecommendationChange: setGapsRecommendation,
              })}
              {hasDpatGapsContent(gapsIssues, gapsRecommendation) && (
                <div style={{ marginTop: 8, textAlign: "right" }}>
                  <Button
                    type="primary"
                    size="small"
                    icon={<SendOutlined />}
                    onClick={() => handleGapsSubmit()}
                    style={{ backgroundColor: DPAT_PRIMARY, borderColor: DPAT_PRIMARY }}
                  >
                    Save capacity gaps
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </Space>
    ) : null;

  const renderCommentInput = () => (
    <div style={{ maxWidth: 720, width: "100%", marginTop: 4 }}>
      {renderCommentActionBar()}
      {renderCommentPanels()}
    </div>
  );

  const roleHeader = (comment, suffix = "") =>
    hideCommentAuthorNames ? (
      <Tag color="blue" style={{ marginInlineEnd: 0 }}>
        {formatDpatCommentUserRole(comment.userRole)}
        {suffix}
      </Tag>
    ) : (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <AntText strong style={{ margin: 0, fontSize: 13 }}>
          {comment.fullName}
          {suffix}
        </AntText>
        <Tag color="blue" style={{ marginInlineEnd: 0 }}>
          {formatDpatCommentUserRole(comment.userRole)}
        </Tag>
      </div>
    );

  const listedComments = comments.filter((c) => {
    const hasText = Boolean(commentTextFromRow(c).trim());
    const hasGaps = commentHasGaps(c);
    if (isDpatQualityAssuranceCommentRole(c.userRole)) {
      return !isDistrictUser && hasText;
    }
    return hasText || hasGaps;
  });

  const renderCommentList = () => (
    <div className="dpat-indicator-feedback__list">
      {listedComments.length === 0 ? (
        <AntText type="secondary" style={{ display: "block", padding: "8px 12px" }}>
          No comments yet.
        </AntText>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            paddingLeft: "12px",
            marginTop: showCommentInput && canShowCommentInput() ? "16px" : "0",
          }}
        >
          {listedComments
            .map((comment) => (
              <Card
                key={comment.id}
                size="small"
                style={{ borderRadius: 10, borderColor: "#e6ebf1" }}
                bodyStyle={{ padding: "12px 14px" }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {/* Comment Section */}
                  {commentTextFromRow(comment).trim() ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {!hideCommentAuthorNames && (
                          <Col>
                            <Avatar
                              src={
                                comment.userImage ||
                                "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTL_JlCFnIGX5omgjEjgV9F3sBRq14eTERK9w&s"
                              }
                              style={{ marginRight: "10px", borderRadius: "50%" }}
                              size={32}
                            />
                          </Col>
                        )}

                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 8,
                              width: "100%",
                            }}
                          >
                            {!hideComment && roleHeader(comment)}
                            {comment.username === currentUsername && !hideComment && canEditComments && (
                              <>
                                <AntText type="secondary" style={{ fontSize: 12 }}>
                                  {formatCommentDate(comment.commentDate)}
                                </AntText>
                                <EditOutlined
                                  style={{ cursor: "pointer", color: "#000000ff" }}
                                  onClick={() => handleEditComment(comment)}
                                />
                              </>
                            )}
                          </div>
                          {editingCommentId === comment.id ? (
                            <div style={{ display: "flex", alignItems: "center", marginTop: "8px" }}>
                              <Input.TextArea
                                value={editText[comment.id] || ""}
                                onChange={(e) =>
                                  setEditText({ ...editText, [comment.id]: e.target.value })
                                }
                                onKeyPress={(e) => handleEditKeyPress(e, comment.id)}
                                autoSize={{ minRows: 3, maxRows: 5 }}
                                style={{
                                  flex: 1,
                                  borderRadius: "10px",
                                  padding: "8px 12px",
                                  background: "#f0f2f5",
                                }}
                              />
                              {editText[comment.id]?.trim() && (
                                <SendOutlined
                                  className="text-blue-500 cursor-pointer"
                                  style={{ fontSize: "20px", marginLeft: "8px" }}
                                  onClick={() => handleCommentSubmit(editText[comment.id])}
                                />
                              )}
                            </div>
                          ) : (
                            <h11 style={{ fontSize: "16px" }}>{commentTextFromRow(comment)}</h11>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  {/* Gaps Section */}
                  {commentHasGaps(comment) ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {!hideCommentAuthorNames && (
                          <Col>
                            <Avatar
                              src={
                                comment.userImage ||
                                "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTL_JlCFnIGX5omgjEjgV9F3sBRq14eTERK9w&s"
                              }
                              style={{ marginRight: "10px", borderRadius: "50%" }}
                              size={32}
                            />
                          </Col>
                        )}
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 8,
                              width: "100%",
                            }}
                          >
                            {!hideComment && roleHeader(comment, " — capacity gaps")}
                            {comment.username === currentUsername && !hideComment && canEditGaps && (
                              <>
                                <AntText type="secondary" style={{ fontSize: 12 }}>
                                  {formatCommentDate(comment.updateDate)}
                                </AntText>
                                <EditOutlined
                                  style={{ cursor: "pointer", color: "#000000ff" }}
                                  onClick={() => handleEditGaps(comment)}
                                />
                              </>
                            )}
                          </div>
                          {editingGapsId === comment.id ? (
                            <div style={{ marginTop: 8 }}>
                              {renderGapsFields({
                                issues: editGapsDraft[comment.id]?.issues || "",
                                recommendation: editGapsDraft[comment.id]?.recommendation || "",
                                onIssuesChange: (value) =>
                                  setEditGapsDraft({
                                    ...editGapsDraft,
                                    [comment.id]: {
                                      ...editGapsDraft[comment.id],
                                      issues: value,
                                    },
                                  }),
                                onRecommendationChange: (value) =>
                                  setEditGapsDraft({
                                    ...editGapsDraft,
                                    [comment.id]: {
                                      ...editGapsDraft[comment.id],
                                      recommendation: value,
                                    },
                                  }),
                                onKeyPress: (e) => handleEditGapsKeyPress(e, comment.id),
                                minRows: 2,
                              })}
                              {hasDpatGapsContent(
                                editGapsDraft[comment.id]?.issues,
                                editGapsDraft[comment.id]?.recommendation,
                              ) && (
                                <div style={{ marginTop: 8, textAlign: "right" }}>
                                  <Button
                                    type="primary"
                                    size="small"
                                    icon={<SendOutlined />}
                                    onClick={() =>
                                      handleGapsSubmit(
                                        editGapsDraft[comment.id]?.issues,
                                        editGapsDraft[comment.id]?.recommendation,
                                      )
                                    }
                                    style={{ backgroundColor: DPAT_PRIMARY, borderColor: DPAT_PRIMARY }}
                                  >
                                    Save capacity gaps
                                  </Button>
                                </div>
                              )}
                            </div>
                          ) : (
                            renderGapsDisplay(comment)
                          )}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </Card>
            ))}
        </div>
      )}
    </div>
  );

  const renderManualScoreSection = () =>
    !hideComment && canViewScoresAndComments ? (
      <DpatManualScorePanel
        districtId={districtId}
        year={year}
        tableCommentedId={tableCommentedId}
        comments={comments}
        data={data}
        manualScoreConfig={manualScoreConfig}
        hideComment={hideComment}
        onCommentsUpdated={setComments}
      />
    ) : null;

  const renderCommentFeedbackFooter = () =>
    !hideComment && canViewScoresAndComments ? (
      <div className="dpat-indicator-feedback">
        {renderManualScoreSection()}
        {renderCommentActionBar()}
        {renderCommentPanels()}
        {renderCommentList()}
      </div>
    ) : null;

  if (typeof children !== "function") {
    return null;
  }

  return children({
    renderCommentInput,
    renderCommentList,
    renderCommentActionBar,
    renderCommentPanels,
    renderManualScoreSection,
    renderCommentFeedbackFooter,
  });
}

export default Comment;